import React, { useMemo, useState } from "react";

import { faXmark } from "@fortawesome/pro-solid-svg-icons";

import { faPen, faTrashCan } from "@performyard/fontawesome/pro-regular-svg-icons";
import { faCirclePlus, faSparkles } from "@performyard/fontawesome/pro-solid-svg-icons";
import { Accordion, ButtonIcon, ButtonStandard, Checkbox, Icon, Text, Tooltip } from "@performyard/py-components";

import InputTextArea from "../../../components/Inputs/InputTextArea";
import {
	CompanyWideChip,
	CompetencyPreviewCard,
	INFERRED_BY_AI_TOOLTIP,
	InferredLevelsLegend,
	hasInferredLevels,
} from "./CompetencyPreviewCard";
import { CreateAllCompetenciesButton } from "./CreateAllCompetenciesButton";

import type { CompetencySuggestion, EditableCompetencyFields } from "../types";

import { useCompetencyFrameworkView } from "../hooks/useCompetencyFrameworkView";

import { MeasureCanvas } from "../../../utils/textMeasurement/MeasureCanvas";

import { useAskPerformYardStore } from "../AskPerformYardStore";

type CompetencyEditPanelProps = {
	onClose: () => void;
};

const ACCORDION_CLOSED_HEIGHT = "h-[58px]";
const CREATE_COMPETENCY_LABEL = "Create Competency";
const CREATE_COMPETENCY_ICON_SIZE = 12;

const CompetencyEditBody: React.FC<{
	suggestion: CompetencySuggestion;
	editedFields: Partial<EditableCompetencyFields> | undefined;
	onChange: (id: string, fields: Partial<EditableCompetencyFields>) => void;
	disabled: boolean;
	itemError?: string;
	isCompanyWide: boolean;
}> = ({ suggestion, editedFields, onChange, disabled, itemError, isCompanyWide }) => {
	const name = editedFields?.name ?? suggestion.name;
	const description = editedFields?.description ?? suggestion.description;
	const levels = editedFields?.levels ?? suggestion.levels;
	const [isEditingName, setIsEditingName] = useState(false);
	const nameInputWidth = useMemo(
		() => new MeasureCanvas().getTextWidth(name || " ", "600 16px Source Sans Pro"),
		[name]
	);

	return (
		<div className="flex min-w-0 flex-col gap-2">
			{itemError && (
				<Text size="caption" color="red-6">
					{itemError}
				</Text>
			)}
			<div className="group flex items-center gap-1">
				{!disabled && isEditingName ? (
					<form
						className="min-w-0"
						onSubmit={(event) => {
							event.preventDefault();
							setIsEditingName(false);
						}}
						onBlur={(event) => {
							if (!event.currentTarget.contains(event.relatedTarget as Node)) {
								setIsEditingName(false);
							}
						}}
					>
						<input
							autoFocus
							className="max-w-full border-0 border-b-[0.5px] border-solid border-neutral-9 bg-transparent p-0 text-base font-semibold text-neutral-9 outline-hidden hover:cursor-text focus:border-primary-4"
							style={{ width: nameInputWidth, minWidth: "100px" }}
							value={name}
							onChange={(event) => onChange(suggestion.id, { name: event.target.value })}
							aria-label="Competency name"
							placeholder="Competency name"
						/>
					</form>
				) : (
					<span
						role="button"
						tabIndex={disabled ? -1 : 0}
						className="min-w-0 py-text-truncate border-0 border-b-[0.5px] border-solid border-transparent text-base font-semibold text-neutral-9 group-hover:border-neutral-9 hover:cursor-text"
						onClick={() => !disabled && setIsEditingName(true)}
						onKeyDown={(event) => {
							if (!disabled && (event.key === "Enter" || event.key === " ")) {
								event.preventDefault();
								setIsEditingName(true);
							}
						}}
					>
						{name || " "}
					</span>
				)}
				{!disabled && (
					<ButtonIcon
						icon={faPen}
						onMouseDown={(event: React.MouseEvent) => event.preventDefault()}
						onClick={() => setIsEditingName((previous) => !previous)}
						variant="standard"
						color="neutral"
						shade={5}
						size={16}
						aria-label="Edit competency name"
					/>
				)}
			</div>
			<InputTextArea
				label="Description"
				labelSize="caption"
				labelWeight="semi-bold"
				value={description}
				disabled={disabled}
				minRows={3}
				onChange={(value) => onChange(suggestion.id, { description: value })}
				id={`competency-description-${suggestion.id}`}
			/>
			<div className="flex items-center gap-1.5">
				<Checkbox
					id={`company-wide-${suggestion.id}`}
					checked={isCompanyWide}
					onCheckedChange={(checked) => onChange(suggestion.id, { isCompanyWide: checked })}
					aria-label="Company wide"
					disabled={disabled}
				/>
				<label
					htmlFor={`company-wide-${suggestion.id}`}
					className={disabled ? "cursor-not-allowed" : "cursor-pointer"}
				>
					<Text size="caption" color="neutral-9">
						Company Wide
					</Text>
				</label>
			</div>
			<div className="mt-2 flex flex-col gap-2">
				<Text size="paragraph-3" weight="semi-bold" color="neutral-9">
					Levels
				</Text>
				{levels.map((level, levelIndex) => (
					<div key={level.rank} className="flex flex-col gap-1">
						<div className="flex items-center gap-1">
							{level.source === "inferred" && (
								<Tooltip content={INFERRED_BY_AI_TOOLTIP}>
									<Icon icon={faSparkles} color="primary-6" size={12} />
								</Tooltip>
							)}
							<Text size="caption" weight="semi-bold" color="neutral-8">
								{level.rank}. {level.name}
							</Text>
						</div>
						<InputTextArea
							value={level.description}
							disabled={disabled}
							minRows={3}
							onChange={(value) => {
								const updatedLevels = levels.map((currentLevel, currentIndex) =>
									currentIndex === levelIndex ? { ...currentLevel, description: value } : currentLevel
								);
								onChange(suggestion.id, { levels: updatedLevels });
							}}
							id={`competency-${suggestion.id}-level-${level.rank}`}
							ariaLabel={`${level.name} level description`}
						/>
					</div>
				))}
			</div>
		</div>
	);
};

export const CompetencyEditPanel: React.FC<CompetencyEditPanelProps> = ({ onClose }) => {
	const updateEditedCompetencyFields = useAskPerformYardStore((state) => state.updateEditedCompetencyFields);
	const { framework, editedFields, itemStatus, visible, pending, isDone, createOne, removeOne, isSaving, bulkSave } =
		useCompetencyFrameworkView();
	const [openIds, setOpenIds] = useState<Set<string>>(new Set());

	if (!framework) return null;

	const { itemErrors } = framework;

	const toggleOpen = (id: string) =>
		setOpenIds((previous) => {
			const next = new Set(previous);
			if (next.has(id)) {
				next.delete(id);
			} else {
				next.add(id);
			}
			return next;
		});
	const expand = (id: string) => setOpenIds((previous) => new Set(previous).add(id));

	const isCompanyWideFor = (suggestion: CompetencySuggestion) =>
		editedFields[suggestion.id]?.isCompanyWide ?? suggestion.isCompanyWide ?? false;

	const editable = visible.filter((suggestion) => itemStatus(suggestion.id) !== "created");
	const createdCount = visible.length - editable.length;
	const allOpen = visible.length > 0 && visible.every((suggestion) => openIds.has(suggestion.id));
	const allCompanyWide = editable.length > 0 && editable.every(isCompanyWideFor);

	const toggleExpandAll = () => setOpenIds(allOpen ? new Set() : new Set(visible.map((suggestion) => suggestion.id)));
	const setAllCompanyWide = (checked: boolean) =>
		editable.forEach((suggestion) => updateEditedCompetencyFields(suggestion.id, { isCompanyWide: checked }));

	const handleCreate = async (suggestion: CompetencySuggestion) => {
		const created = await createOne(suggestion);
		if (!created) {
			expand(suggestion.id);
		}
	};

	return (
		<div className="flex h-full min-w-0 flex-col overflow-hidden">
			<div className="box-border flex w-full shrink-0 items-center gap-2 bg-white p-2 pr-6">
				<div className="flex min-w-0 flex-1 items-center">
					<div className="flex items-center justify-center p-2">
						<ButtonIcon
							icon={faXmark}
							onClick={onClose}
							variant="standard"
							color="neutral"
							shade={5}
							size={20}
							aria-label="Close edit panel"
						/>
					</div>
					<Text size="paragraph-3" weight="semi-bold" color="neutral-9">
						Edit Competencies
					</Text>
				</div>
				<CreateAllCompetenciesButton
					isDone={isDone}
					isSaving={isSaving}
					disabled={isSaving || pending.length === 0}
					onCreateAll={bulkSave}
				/>
			</div>

			<div className="min-w-0 flex-1 overflow-y-auto bg-white px-4 pt-4 pb-6">
				<div className="flex min-w-0 flex-col gap-2 px-7">
					{visible.length === 0 ? (
						<Text size="paragraph-3" color="neutral-7">
							All suggested competencies have been removed.
						</Text>
					) : (
						<>
							<div className="flex flex-col gap-2 pr-10">
								<div className="flex items-center justify-between gap-2">
									<Text size="caption" color="neutral-6">
										{visible.length} {visible.length === 1 ? "competency" : "competencies"}
										{createdCount > 0 ? ` · ${createdCount} created` : ""}
									</Text>
									<div className="flex items-center gap-3">
										<div className="flex items-center gap-1.5">
											<Checkbox
												id="make-all-company-wide"
												checked={allCompanyWide}
												onCheckedChange={setAllCompanyWide}
												aria-label="Make all company wide"
												disabled={isSaving || editable.length === 0}
											/>
											<label htmlFor="make-all-company-wide" className="cursor-pointer">
												<Text size="caption" color="neutral-6">
													Make all company wide
												</Text>
											</label>
										</div>
										<ButtonStandard
											variant="text"
											color="neutral"
											size="small"
											onClick={toggleExpandAll}
											disabled={visible.length === 0}
											dangerouslySetClassName="!w-auto shrink-0 whitespace-nowrap"
										>
											{allOpen ? "Collapse all" : "Expand all"}
										</ButtonStandard>
									</div>
								</div>

								{hasInferredLevels(visible, editedFields) && <InferredLevelsLegend />}
							</div>

							{visible.map((suggestion) => {
								const currentStatus = itemStatus(suggestion.id);
								const displayName = editedFields[suggestion.id]?.name ?? suggestion.name;
								const levelCount = (editedFields[suggestion.id]?.levels ?? suggestion.levels).length;
								const companyWide = isCompanyWideFor(suggestion);
								const isCreated = currentStatus === "created";
								const isOpen = openIds.has(suggestion.id);
								return (
									<div key={suggestion.id} className="flex items-start gap-2">
										<div className="min-w-0 flex-1">
											{isCreated ? (
												<CompetencyPreviewCard
													name={displayName}
													description={
														editedFields[suggestion.id]?.description ??
														suggestion.description
													}
													levels={editedFields[suggestion.id]?.levels ?? suggestion.levels}
													itemStatus="created"
													itemError={itemErrors[suggestion.id]}
													saving={isSaving}
													isCompanyWide={companyWide}
													onCreate={() => {}}
													onRemove={() => {}}
												/>
											) : (
												<div className="relative">
													<Accordion
														data-testid={`competency-accordion-${suggestion.id}`}
														variant="standard"
														size="small"
														isOpen={isOpen}
														onToggle={() => toggleOpen(suggestion.id)}
														headerContent={
															<div className="flex min-w-0 flex-1 items-center justify-between gap-3">
																<div className="flex min-w-0 flex-1 flex-col">
																	<Text
																		size="paragraph-3"
																		weight="semi-bold"
																		color="neutral-9"
																		wrap={false}
																		dangerouslySetClassName="py-text-truncate"
																	>
																		{displayName}
																	</Text>
																	<Text size="caption" color="neutral-6">
																		{levelCount}{" "}
																		{levelCount === 1 ? "level" : "levels"}
																	</Text>
																</div>
																{companyWide && <CompanyWideChip />}
																{/* Reserves the width of the Create action that floats over this row. */}
																<span
																	aria-hidden="true"
																	className="invisible flex shrink-0 items-center gap-1 text-caption font-semibold whitespace-nowrap"
																>
																	<Icon
																		icon={faCirclePlus}
																		size={CREATE_COMPETENCY_ICON_SIZE}
																	/>
																	{CREATE_COMPETENCY_LABEL}
																</span>
															</div>
														}
													>
														<CompetencyEditBody
															suggestion={suggestion}
															editedFields={editedFields[suggestion.id]}
															onChange={updateEditedCompetencyFields}
															itemError={itemErrors[suggestion.id]}
															isCompanyWide={companyWide}
															disabled={isSaving || currentStatus === "creating"}
														/>
													</Accordion>
													<div
														className={`pointer-events-none absolute right-10 flex items-center gap-3 ${
															isOpen ? `top-0 ${ACCORDION_CLOSED_HEIGHT}` : "inset-y-0"
														}`}
													>
														{currentStatus === "creating" ? (
															<Text size="caption" color="neutral-6">
																Creating…
															</Text>
														) : (
															<div className="pointer-events-auto flex items-center">
																<ButtonStandard
																	variant="text"
																	color="primary"
																	size="small"
																	iconLeft={faCirclePlus}
																	onClick={() => handleCreate(suggestion)}
																	disabled={isSaving}
																	dangerouslySetClassName="!w-auto shrink-0 border-0 whitespace-nowrap"
																>
																	{CREATE_COMPETENCY_LABEL}
																</ButtonStandard>
															</div>
														)}
													</div>
												</div>
											)}
										</div>
										<div
											className={`flex w-8 shrink-0 items-center justify-center ${ACCORDION_CLOSED_HEIGHT}`}
										>
											{!isCreated && currentStatus !== "creating" && (
												<ButtonIcon
													icon={faTrashCan}
													onClick={() => removeOne(suggestion.id)}
													variant="standard"
													color="neutral"
													shade={5}
													size={20}
													aria-label="Remove"
													disabled={isSaving}
												/>
											)}
										</div>
									</div>
								);
							})}
						</>
					)}
				</div>
			</div>
		</div>
	);
};
